<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */

// post : champs hétérogènes selon la source (SSR ou endpoint paginé) → type
// indexable, cohérent avec ModalItem du parent. Les champs lus : id, title,
// content, image, date_created.
 
type PostItem = Record<string, any>;

const props = defineProps<{
  post: PostItem | null;
  getImageUrl: (fileId: string | null, width?: number, height?: number) => string | null;
  formatDate: (dateStr: string | null) => string | null;
}>();

defineEmits<{
  close: [];
}>();

// Scroll de la page verrouillé tant que l'article est ouvert (verrou partagé
// à compteur — cette modale peut s'empiler sur « Toutes les actualités »).
// Karim 2026-08-05.
useBodyScrollLock().bindToRef(() => props.post);
</script>

<template>
  <Teleport to="body">
    <Transition name="post-modal-fade">
      <div v-if="post" class="post-modal-overlay" @click.self="$emit('close')">
        <div class="post-modal-panel">
          <!-- Bouton fermer flottant (au-dessus de l'image) -->
          <button class="post-modal-close" @click="$emit('close')">
            <UIcon name="i-lucide-x" class="size-5" />
          </button>

          <div class="post-modal-scroll">
            <!-- Image de couverture -->
            <img
              v-if="post.image"
              :src="getImageUrl(post.image, 1200, 675)!"
              :alt="post.title"
              class="post-modal-cover"
            />

            <div class="post-modal-content">
              <!-- Date -->
              <div class="flex items-center gap-2 text-sm text-gray-400 mb-3">
                <UIcon name="i-lucide-calendar" class="size-4" />
                <span>{{ formatDate(post.date_created) }}</span>
              </div>

              <!-- Titre -->
              <h2 class="text-2xl font-bold text-gray-900 mb-5 leading-tight">
                {{ post.title }}
              </h2>

              <!-- Contenu complet formaté -->
              <!-- eslint-disable vue/no-v-html -->
              <!-- Reason: post.content est RICH-TEXT sanitizé côté backend Donoor -->
              <div
                v-if="post.content"
                class="prose prose-gray max-w-none post-modal-prose"
                v-html="post.content"
              />
              <!-- eslint-enable vue/no-v-html -->
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<!--
  Styles non-scoped : la modale est teleport-ée dans <body> (hors scope CSS).
  Préfixe `post-modal-` spécifique pour éviter toute collision.
-->
<style>
.post-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 210;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
}
@media (min-width: 640px) {
  .post-modal-overlay {
    align-items: center;
    padding: 1.5rem;
  }
}

.post-modal-panel {
  position: relative;
  background: white;
  width: 100%;
  max-width: 680px;
  max-height: 92vh;
  border-radius: 24px 24px 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
@media (min-width: 640px) {
  .post-modal-panel {
    border-radius: 24px;
  }
}

.post-modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 2;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  color: #374151;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  transition: background 0.2s;
}
.post-modal-close:hover {
  background: #fff;
}

.post-modal-scroll {
  overflow-y: auto;
  flex: 1;
  /* Le scroll reste DANS la modale (pas de chaînage vers la page/modale
     derrière en fin de contenu). Karim 2026-08-05. */
  overscroll-behavior: contain;
}

.post-modal-cover {
  width: 100%;
  height: auto;
  max-height: 340px;
  object-fit: cover;
  display: block;
}

.post-modal-content {
  padding: 1.5rem;
}
@media (min-width: 640px) {
  .post-modal-content {
    padding: 2rem;
  }
}

/* Contenu riche : images fluides, espacements lisibles */
.post-modal-prose :deep(img),
.post-modal-prose img {
  max-width: 100%;
  height: auto;
  border-radius: 0.75rem;
  margin: 1rem 0;
}
.post-modal-prose p {
  margin: 0 0 0.75rem;
  line-height: 1.7;
}

.post-modal-fade-enter-active,
.post-modal-fade-leave-active {
  transition: opacity 0.25s ease;
}
.post-modal-fade-enter-active .post-modal-panel,
.post-modal-fade-leave-active .post-modal-panel {
  transition: transform 0.25s ease;
}
.post-modal-fade-enter-from,
.post-modal-fade-leave-to {
  opacity: 0;
}
.post-modal-fade-enter-from .post-modal-panel,
.post-modal-fade-leave-to .post-modal-panel {
  transform: translateY(20px);
}
</style>
